import React, { useMemo, useState } from "react";
import { Checkbox } from "@chakra-ui/react";
import { FiAlertTriangle } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { ProfileAvatar } from "newui/components/ProfileAvatar/ProfileAvatar";
import type { AwardRow, AwardStatus } from "../../../../types/award.type";
import { useResendAwardEmail } from "../../../../hooks/useResendAwardEmail";
import { ResponseAccessActions } from "./ResponseAccessActions";
import { CompanyLogoDisplay } from "newui/features/Company/components/CompanyLogo";

/**
 * Canonical, user-facing status labels. Defined as a constant to prevent string
 * drift — the word "Declined" must never appear (PRO-572). PRO-655 supersedes
 * the earlier "Not Awarded" wording with "Unsuccessful".
 */
export const AWARD_STATUS_LABELS: Record<AwardStatus, string> = {
	pending: "Pending Decision",
	awarded: "Preferred",
	unsuccessful: "Unsuccessful",
};

const STATUS_CHIP_STYLES: Record<AwardStatus, string> = {
	pending: "bg-gray-100 text-gray-700",
	awarded: "bg-green-100 text-green-700",
	unsuccessful: "bg-red-100 text-red-700",
};

const isDecided = (row: AwardRow): boolean => row.award_status !== "pending";

const isLocked = (row: AwardRow): boolean =>
	row.invite_status === "withdrawn" || row.invite_status === "revoked";

/** A supplier can be actioned only when undecided and not withdrawn/revoked. */
const isActionable = (row: AwardRow): boolean => !isDecided(row) && !isLocked(row);

interface StatusChipProps {
	status: AwardStatus;
}

const StatusChip = ({ status }: StatusChipProps) => {
	const label = AWARD_STATUS_LABELS[status];
	return (
		<span
			role="status"
			tabIndex={0}
			aria-label={`Award status: ${label}`}
			data-testid={`status-chip-${status}`}
			className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${STATUS_CHIP_STYLES[status]}`}
		>
			{label}
		</span>
	);
};

interface SupplierResultsTableProps {
	awards: AwardRow[];
	evalId?: number;
	/** FIX-007: buyer-company admins get read access — no decision actions. */
	readOnly?: boolean;
	rfxId?: number;
	onAward?: (row: AwardRow) => void;
	onMarkNotAwarded?: (rows: AwardRow[]) => void;
}

export const SupplierResultsTable = ({
	awards,
	rfxId,
	evalId,
	onAward,
	onMarkNotAwarded,
	readOnly = false,
}: SupplierResultsTableProps) => {
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [resendingId, setResendingId] = useState<number | null>(null);
	const resendEmail = useResendAwardEmail(rfxId, evalId);

	const handleResend = (inviteId: number) => {
		setResendingId(inviteId);
		resendEmail.mutate(inviteId, {
			onSettled: () => setResendingId(null),
		});
	};

	// Rank ascending (rank 1 first); rows without a rank sort to the bottom.
	const rankedAwards = useMemo(() => {
		return [...awards].sort((a, b) => {
			const aRank = a.rank ?? Number.POSITIVE_INFINITY;
			const bRank = b.rank ?? Number.POSITIVE_INFINITY;
			return aRank - bRank;
		});
	}, [awards]);

	const selectableRows = useMemo(
		() => rankedAwards.filter(isActionable),
		[rankedAwards]
	);

	const selectedRows = useMemo(
		() => rankedAwards.filter((row) => selectedIds.has(row.rfx_invite_id)),
		[rankedAwards, selectedIds]
	);

	const allSelectableSelected =
		selectableRows.length > 0 &&
		selectableRows.every((row) => selectedIds.has(row.rfx_invite_id));

	const toggleRow = (inviteId: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(inviteId)) {
				next.delete(inviteId);
			} else {
				next.add(inviteId);
			}
			return next;
		});
	};

	const toggleSelectAll = () => {
		setSelectedIds((prev) => {
			if (prev.size > 0) {
				return new Set();
			}
			return new Set(selectableRows.map((row) => row.rfx_invite_id));
		});
	};

	const handleBulkMarkNotAwarded = () => {
		const actionable = selectedRows.filter(isActionable);
		if (actionable.length > 0 && onMarkNotAwarded) {
			onMarkNotAwarded(actionable);
		}
	};

	const bulkActionEnabled = selectedRows.some(isActionable);

	if (rankedAwards.length === 0) {
		return (
			<div
				data-testid="supplier-results-empty"
				className="text-sm text-gray-500 py-6 text-center"
			>
				No suppliers to display yet.
			</div>
		);
	}

	return (
		<div data-testid="supplier-results-table">
			{!readOnly && (
				<div className="flex justify-end mb-3">
					<Button
						type="button"
						btntype="secondary"
						size="sm"
						disabled={!bulkActionEnabled}
						disabledStyle={!bulkActionEnabled}
						onClick={handleBulkMarkNotAwarded}
						data-testid="bulk-mark-not-awarded"
					>
						Unsuccessful
					</Button>
				</div>
			)}
			<div className="overflow-x-auto">
				<table className="w-full text-sm border-collapse">
					<thead>
						<tr className="border-b border-gray-200">
							{!readOnly && (
								<th className="py-2 px-2 w-10">
									<Checkbox
										isChecked={allSelectableSelected}
										isDisabled={selectableRows.length === 0}
										onChange={toggleSelectAll}
										aria-label="Select all undecided suppliers"
										data-testid="select-all-checkbox"
									/>
								</th>
							)}
							<th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
								Rank
							</th>
							<th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
								Supplier
							</th>
							<th className="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
								Final Score
							</th>
							<th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
								Status
							</th>
							<th className="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{rankedAwards.map((row) => {
							const decided = isDecided(row);
							const locked = isLocked(row);
							const actionable = isActionable(row);

							return (
								<tr
									key={row.rfx_invite_id}
									className="border-b border-gray-100"
									data-testid={`supplier-row-${row.rfx_invite_id}`}
								>
									{!readOnly && (
										<td className="py-2 px-2">
											<Checkbox
												isChecked={selectedIds.has(row.rfx_invite_id)}
												isDisabled={!actionable}
												onChange={() => toggleRow(row.rfx_invite_id)}
												aria-label={`Select ${row.supplier.name}`}
												data-testid={`select-checkbox-${row.rfx_invite_id}`}
											/>
										</td>
									)}
									<td className="py-2 px-2 text-gray-700">
										{row.rank ?? "—"}
									</td>
									<td className="py-2 px-2 text-gray-900 font-medium">
										<span className="flex items-center gap-2">
											<CompanyLogoDisplay size="sm" supplierLogoUrl={row.supplier.logo} supplierName={row.supplier.name} />
											{row.supplier.name}
										</span>
										{locked && (
											<span className="ml-2 text-xs text-gray-400">
												({row.invite_status})
											</span>
										)}
									</td>
									<td className="py-2 px-2 text-right text-gray-700 tabular-nums">
										{row.final_score ?? "—"}
									</td>
									<td className="py-2 px-2">
										<StatusChip status={row.award_status} />
									</td>
									<td className="py-2 px-2">
										{!decided ? (
											readOnly ? (
												<div className="text-right text-gray-400">—</div>
											) : (
												<div className="flex justify-end gap-2">
													<Button
														type="button"
														btntype="secondary"
														size="xs"
														disabled={!actionable}
														disabledStyle={!actionable}
														onClick={() => onAward?.(row)}
														data-testid={`award-btn-${row.rfx_invite_id}`}
													>
														Preferred
													</Button>
													<Button
														type="button"
														btntype="secondary"
														size="xs"
														disabled={!actionable}
														disabledStyle={!actionable}
														onClick={() => onMarkNotAwarded?.([row])}
														data-testid={`not-awarded-btn-${row.rfx_invite_id}`}
													>
														Unsuccessful
													</Button>
												</div>
											)
										) : (
											<div className="flex flex-col items-end gap-1">
												{rfxId != null && (
													<ResponseAccessActions
														rfxId={rfxId}
														inviteId={row.rfx_invite_id}
													/>
												)}
												{/* VT-031: award email dispatch failure — surface a
												    retry-send using the delivered useResendAwardEmail. */}
												{!readOnly &&
													row.award_status === "awarded" &&
													row.email_status === "failed" && (
														<div
															className="flex items-center gap-2"
															data-testid={`email-failed-${row.rfx_invite_id}`}
														>
															<span className="flex items-center gap-1 text-xs text-red-600">
																<FiAlertTriangle /> Email failed to send
															</span>
															<Button
																type="button"
																btntype="plain-color"
																size="xs"
																isLoading={resendingId === row.rfx_invite_id}
																onClick={() => handleResend(row.rfx_invite_id)}
																data-testid={`resend-email-${row.rfx_invite_id}`}
															>
																Resend email
															</Button>
														</div>
													)}
											</div>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};
